import { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FiEdit3 } from 'react-icons/fi';
import { IoChevronBack, IoChevronForward, IoChevronDown } from 'react-icons/io5';
import { LuMoon, LuClock, LuCopy } from 'react-icons/lu';
import type { IReservationListItem, ReservationTab, ITaskManagementDashboardItem } from '../types';
import { ChannelLogo } from '../../channels/components/ChannelLogos';
import type { ChannelLogoType } from '../../channels/components/ChannelLogos';
import ReservationFilters from './ReservationFilters';
import ConfirmCheckoutModal from './ConfirmCheckoutModal';

// --- Checkbox ---
const Checkbox = ({ checked, onChange }: { checked: boolean; onChange: () => void }) => (
  <Box
    onClick={(e) => { e.stopPropagation(); onChange(); }}
    sx={{
      width: '20px',
      height: '20px',
      borderRadius: '3px',
      border: checked ? 'none' : '1px solid #EBECED',
      backgroundColor: checked ? '#5B3FFF' : '#FFFFFF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      flexShrink: 0,
    }}
  >
    {checked && (
      <svg width="9" height="6" viewBox="0 0 9 6" fill="none">
        <path d="M1 3L3.5 5.5L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </Box>
);

// --- Avatar ---
const Avatar = ({ name }: { name: string | null }) => {
  const initial = name ? name.charAt(0).toUpperCase() : '?';
  const colors = ['#5B3FFF', '#E91E63', '#FF9800', '#4CAF50', '#2196F3', '#9C27B0'];
  const colorIndex = name ? name.charCodeAt(0) % colors.length : 0;
  return (
    <Box
      sx={{
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: colors[colorIndex],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '10px', fontWeight: 700, color: '#FFFFFF', lineHeight: 1 }}>
        {initial}
      </Typography>
    </Box>
  );
};

// Format date string: strip ISO suffix and convert to MM/DD
const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}/${dd}`;
  } catch {
    return dateStr;
  }
};

// Format ISO datetime or time string to Arabic 12h: "ص 08:00" or "م 02:00"
// Extracts time directly from the string to avoid browser timezone conversion.
const formatTimeDisplay = (timeStr: string | null): string => {
  if (!timeStr) return '';
  try {
    let hours: number;
    let minutes: number;
    if (timeStr.includes('T')) {
      // Extract HH:mm from the ISO string directly (e.g. "2026-03-28T00:00:00+03:00" → 00:00)
      const timePart = timeStr.split('T')[1]; // "00:00:00+03:00"
      const hm = timePart.split(':');
      hours = parseInt(hm[0], 10);
      minutes = parseInt(hm[1], 10);
    } else if (timeStr.includes(':')) {
      const parts = timeStr.split(':');
      hours = parseInt(parts[0], 10);
      minutes = parseInt(parts[1], 10);
    } else {
      return timeStr;
    }
    if (isNaN(hours) || isNaN(minutes)) return timeStr;
    const period = hours < 12 ? 'ص' : 'م';
    const h12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${period} ${String(h12).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  } catch {
    return timeStr;
  }
};

interface IReservationTableProps {
  activeTab: ReservationTab;
  items: IReservationListItem[];
  totalCount: number;
  isLoading: boolean;
  activePage: number;
  perPage: string;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: string) => void;
  onRowClick?: (item: IReservationListItem) => void;
  onConfirmCheckout?: (reservationId: number) => void;
  isConfirmingCheckout?: boolean;
  onCreateDirectBooking?: (item: ITaskManagementDashboardItem) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  bookingStatusFilter?: string;
  onBookingStatusChange?: (value: string | undefined) => void;
  propertyIdFilter?: number;
  onPropertyIdChange?: (value: number | undefined) => void;
}

// --- Status Badge ---
const StatusBadge = ({ status, type }: { status: string | null; type: 'booking' | 'property' }) => {
  if (!status) return <span>—</span>;

  const getStyles = () => {
    const s = status.trim();
    if (type === 'booking') {
      if (s === 'مؤكد' || s === 'Confirmed')
        return { bg: 'rgba(41,181,53,0.14)', color: '#29b535' };
      if (s === 'معدّل' || s === 'Modified')
        return { bg: 'rgba(41,181,53,0.14)', color: '#29b535' };
      if (s === 'بانتظار السداد' || s === 'Pending Payment')
        return { bg: '#FFF3E0', color: '#E65100' };
      return { bg: '#F3F4F6', color: '#646E82' };
    }
    // property status
    if (s === 'جاهز للدخول' || s === 'Ready')
      return { bg: 'rgba(41,181,53,0.14)', color: '#29b535' };
    if (s === 'قيد التنظيف' || s === 'Cleaning')
      return { bg: '#FFF3E0', color: '#E65100' };
    if (s === 'بانتظار التنظيف' || s === 'Waiting')
      return { bg: '#F3F4F6', color: '#646E82' };
    return { bg: '#F3F4F6', color: '#646E82' };
  };

  const styles = getStyles();

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: '12px',
        py: '2px',
        borderRadius: '8px',
        backgroundColor: styles.bg,
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Tajawal, sans-serif',
          fontSize: '12px',
          fontWeight: 500,
          color: styles.color,
          whiteSpace: 'nowrap',
        }}
      >
        {status}
      </Typography>
    </Box>
  );
};

// --- Column config per tab ---
type ColumnDef = { label: string; flex: string };

const getColumnsForTab = (tab: ReservationTab, t: (key: string) => string): ColumnDef[] => {
  switch (tab) {
    case 'upcoming':
      return [
        { label: t('TaskManagemnt.guestName'), flex: '2.5' },
        { label: t('TaskManagemnt.property'), flex: '2' },
        { label: t('TaskManagemnt.stayPeriod'), flex: '2.5' },
        { label: t('TaskManagemnt.bookingStatus'), flex: '1.5' },
        { label: t('TaskManagemnt.totalBooking'), flex: '1.5' },
        { label: '', flex: '0 0 40px' },
      ];
    case 'checkin_today':
      return [
        { label: t('TaskManagemnt.guestName'), flex: '2' },
        { label: t('TaskManagemnt.property'), flex: '1.5' },
        { label: t('TaskManagemnt.stayPeriod'), flex: '2.5' },
        { label: t('TaskManagemnt.bookingStatus'), flex: '1.5' },
        { label: t('TaskManagemnt.propertyStatus'), flex: '1.5' },
        { label: t('TaskManagemnt.totalBooking'), flex: '1.5' },
        { label: '', flex: '0 0 40px' },
      ];
    case 'checkout_today':
      return [
        { label: t('TaskManagemnt.guestName'), flex: '2' },
        { label: t('TaskManagemnt.property'), flex: '1.5' },
        { label: t('TaskManagemnt.stayPeriod'), flex: '2.5' },
        { label: t('TaskManagemnt.bookingStatus'), flex: '1.5' },
        { label: t('TaskManagemnt.totalBooking'), flex: '1.5' },
        { label: '', flex: '2' },
      ];
    case 'vacant':
      return [
        { label: t('TaskManagemnt.property'), flex: '2' },
        { label: t('TaskManagemnt.propertyStatus'), flex: '1.5' },
        { label: t('TaskManagemnt.lastCheckout'), flex: '2' },
        { label: t('TaskManagemnt.nextReservation'), flex: '2' },
        { label: t('TaskManagemnt.vacantDuration'), flex: '1.5' },
        { label: '', flex: '2' },
      ];
    default:
      return [];
  }
};

// --- Table Header ---
const TableHeader = ({ allSelected, onSelectAll, activeTab }: { allSelected: boolean; onSelectAll: () => void; activeTab: ReservationTab }) => {
  const { t } = useTranslation();
  const columns = getColumnsForTab(activeTab, t);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: '16px',
        py: '16px',
      }}
    >
      {columns.map((col, i) => (
        <Box key={i} sx={{ flex: col.flex, minWidth: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'flex-start' }}>
          {i === 0 && <Checkbox checked={allSelected} onChange={onSelectAll} />}
          <Typography
            sx={{
              fontFamily: 'Tajawal, sans-serif',
              fontSize: '12px',
              fontWeight: 500,
              color: '#8E98A1',
              textAlign: 'start',
            }}
          >
            {col.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// --- Channel logo mapping from platFormSRC enum ---
const getChannelLogoType = (platFormSRC: number): ChannelLogoType | null => {
  switch (platFormSRC) {
    case 1: return 'airbnb';
    case 2: return 'gathern';
    case 3: return 'booking';
    case 6: return 'msool';
    default: return null;
  }
};

const getChannelName = (platFormSRC: number): string => {
  switch (platFormSRC) {
    case 1: return 'Airbnb';
    case 2: return 'GatherIn';
    case 3: return 'Booking';
    case 4: return 'Agoda';
    case 5: return 'Other';
    case 6: return 'Msool';
    case 7: return 'Website';
    default: return '';
  }
};

// --- Shared cell renderers ---
const GuestNameCell = ({ item, selected, onSelect, flex }: { item: IReservationListItem; selected: boolean; onSelect: () => void; flex: string }) => (
  <Box sx={{ flex, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-start' }}>
    <Checkbox checked={selected} onChange={onSelect} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', fontWeight: 400, color: '#435363', lineHeight: '21px' }}>
        {item.guestName || '—'}
      </Typography>
      <ChannelIcon platFormSRC={item.platFormSRC} />
    </Box>
  </Box>
);

const ChannelIcon = ({ platFormSRC }: { platFormSRC: number }) => {
  const logoType = getChannelLogoType(platFormSRC);
  const channelName = getChannelName(platFormSRC);
  if (!channelName) return null;
  if (logoType) {
    return <ChannelLogo channel={logoType} size="xs" />;
  }
  // Fallback text badge for channels without logos (Agoda, Other, Website)
  return (
    <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '10px', fontWeight: 500, color: '#5B3FFF', backgroundColor: '#F3F0FF', px: '4px', py: '1px', borderRadius: '4px', whiteSpace: 'nowrap', lineHeight: 1.4 }}>
      {channelName}
    </Typography>
  );
};

const truncateId = (id: string | null, maxLen = 4): string => {
  if (!id) return '';
  return id.length > maxLen ? id.slice(0, maxLen) + '...' : id;
};

const PropertyCell = ({ item, flex }: { item: IReservationListItem; flex: string }) => (
  <Box sx={{ flex, minWidth: 0 }}>
    <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', fontWeight: 400, color: '#435363' }}>
      {item.propertyName}
    </Typography>
    {item.reservationId && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: '2px' }}>
        <LuCopy size={12} color="#8E98A1" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(item.reservationId || ''); }} />
        <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '12px', color: '#8E98A1' }}>
          {truncateId(item.reservationId)}
        </Typography>
      </Box>
    )}
  </Box>
);

const PropertyCellWithCheckbox = ({ item, selected, onSelect, flex }: { item: IReservationListItem; selected: boolean; onSelect: () => void; flex: string }) => (
  <Box sx={{ flex, minWidth: 0, display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'flex-start' }}>
    <Checkbox checked={selected} onChange={onSelect} />
    <Box>
      <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', fontWeight: 400, color: '#435363' }}>
        {item.propertyName}
      </Typography>
      {item.reservationId && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: '2px' }}>
          <LuCopy size={12} color="#8E98A1" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(item.reservationId || ''); }} />
          <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '12px', color: '#8E98A1' }}>
            {truncateId(item.reservationId)}
          </Typography>
        </Box>
      )}
    </Box>
  </Box>
);

const StayPeriodCell = ({ item, flex }: { item: IReservationListItem; flex: string }) => (
  <Box sx={{ flex, minWidth: 0 }}>
    {item.checkIn && item.checkOut ? (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {/* Dates: checkOut → checkIn | nights — matches RTL Figma order */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '13px', color: '#435363' }}>
            {formatDate(item.checkIn)}  <svg width="9" height="5" viewBox="0 0 9 5" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M2.48393 -0.000102092L3.07193 0.575898L2.02793 1.6079L1.42793 2.1119L1.43993 2.1479L2.60393 2.0759L8.51993 2.0759L8.51993 2.8919L2.60393 2.8919L1.43993 2.8199L1.42793 2.8559L2.02793 3.3599L3.07193 4.3919L2.48393 4.9679L-6.81326e-05 2.4839L2.48393 -0.000102092Z" fill="#CCCCCC"/>
</svg>
 {formatDate(item.checkOut)} 
          </Typography>
          <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '13px', color: '#cccccc' }}>|</Typography>
          {item.numberOfNights > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '11px', fontWeight: 500, color: '#435363' }}>
                {item.numberOfNights}
              </Typography>
              <LuMoon size={12} color="#8E98A1" />
            </Box>
          )}
        </Box>
        {/* Time pill: checkin time → checkout time, clock icon on the end */}
        {item.checkInTimeDisplay && item.checkOutTimeDisplay && (
          <Box sx={{ width: 'max-content', display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: '#F5F5F5', px: '8px', py: '2px', borderRadius: '12px' }}>
            <LuClock size={12} color="#8E98A1" />
            <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '12px', color: '#435363' }}>
              {formatTimeDisplay(item.checkInTimeDisplay)} <svg width="9" height="5" viewBox="0 0 9 5" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M2.48393 -0.000102092L3.07193 0.575898L2.02793 1.6079L1.42793 2.1119L1.43993 2.1479L2.60393 2.0759L8.51993 2.0759L8.51993 2.8919L2.60393 2.8919L1.43993 2.8199L1.42793 2.8559L2.02793 3.3599L3.07193 4.3919L2.48393 4.9679L-6.81326e-05 2.4839L2.48393 -0.000102092Z" fill="#CCCCCC"/>
</svg>
 {formatTimeDisplay(item.checkOutTimeDisplay)}
            </Typography>
          </Box>
        )}
      </Box>
    ) : (
      <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '13px', color: '#8E98A1' }}>—</Typography>
    )}
  </Box>
);

const BookingStatusCell = ({ item, flex }: { item: IReservationListItem; flex: string }) => (
  <Box sx={{ flex, minWidth: 0, display: 'flex', justifyContent: 'flex-start' }}>
    <StatusBadge status={item.bookingStatus} type="booking" />
  </Box>
);

const PropertyStatusCell = ({ item, flex }: { item: IReservationListItem; flex: string }) => (
  <Box sx={{ flex, minWidth: 0, display: 'flex', justifyContent: 'flex-start' }}>
    <StatusBadge status={item.propertyStatus} type="property" />
  </Box>
);

const TotalPriceCell = ({ item, flex }: { item: IReservationListItem; flex: string }) => (
  <Box sx={{ flex, minWidth: 0, display: 'flex', justifyContent: 'flex-start' }}>
    {item.totalPrice > 0 ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
        <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', fontWeight: 700, color: '#666666', lineHeight: '18px' }}>
          {item.totalPrice.toLocaleString()}
        </Typography>
        <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '11px', color: '#666666', lineHeight: '18px' }}>
          ر.س
        </Typography>
      </Box>
    ) : (
      <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', color: '#435363' }}>—</Typography>
    )}
  </Box>
);

const EditActionCell = ({ item, onRowClick }: { item: IReservationListItem; onRowClick?: (item: IReservationListItem) => void }) => (
  <Box sx={{ flex: '0 0 32px' }}>
    <IconButton size="small" sx={{ p: '4px' }} onClick={(e) => { e.stopPropagation(); onRowClick?.(item); }}>
      <FiEdit3 size={18} color="#8E98A1" />
    </IconButton>
  </Box>
);

// --- Table Row ---
const TableRow = ({
  item,
  selected,
  onSelect,
  onRowClick,
  onShowConfirmModal,
  onCreateDirectBooking,
  activeTab,
}: {
  item: IReservationListItem;
  selected: boolean;
  onSelect: () => void;
  onRowClick?: (item: IReservationListItem) => void;
  onShowConfirmModal?: (item: IReservationListItem) => void;
  onCreateDirectBooking?: (item: ITaskManagementDashboardItem) => void;
  activeTab: ReservationTab;
}) => {
  const { t } = useTranslation();

  const rowSx = {
    display: 'flex',
    alignItems: 'center',
    px: '16px',
    py: '16px',
    my: '12px',
    border: '1px solid #E5E7EB',
    '&:hover': { backgroundColor: '#FAFAFA' },
    minHeight: '85px',
    borderRadius: '12px',
  };

  if (activeTab === 'upcoming') {
    return (
      <Box sx={rowSx}>
        <GuestNameCell item={item} selected={selected} onSelect={onSelect} flex="2.5" />
        <PropertyCell item={item} flex="2" />
        <StayPeriodCell item={item} flex="2.5" />
        <BookingStatusCell item={item} flex="1.5" />
        <TotalPriceCell item={item} flex="1.5" />
        <EditActionCell item={item} onRowClick={onRowClick} />
      </Box>
    );
  }

  if (activeTab === 'checkin_today') {
    return (
      <Box sx={rowSx}>
        <GuestNameCell item={item} selected={selected} onSelect={onSelect} flex="2" />
        <PropertyCell item={item} flex="1.5" />
        <StayPeriodCell item={item} flex="2.5" />
        <BookingStatusCell item={item} flex="1.5" />
        <PropertyStatusCell item={item} flex="1.5" />
        <TotalPriceCell item={item} flex="1.5" />
        <EditActionCell item={item} onRowClick={onRowClick} />
      </Box>
    );
  }

  if (activeTab === 'checkout_today') {
    return (
      <Box sx={rowSx}>
        <GuestNameCell item={item} selected={selected} onSelect={onSelect} flex="2" />
        <PropertyCell item={item} flex="1.5" />
        <StayPeriodCell item={item} flex="2.5" />
        <BookingStatusCell item={item} flex="1.5" />
        <TotalPriceCell item={item} flex="1.5" />
        {/* Confirm checkout button + edit icon — purple outlined per Figma */}
        <Box sx={{ flex: '2', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Box
            onClick={(e) => { e.stopPropagation(); onShowConfirmModal?.(item); }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: '24px',
              height: '32px',
              borderRadius: '10px',
              border: '1px solid #5B3FFF',
              cursor: 'pointer',
              '&:hover': { backgroundColor: '#F3F0FF' },
            }}
          >
            <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '12px', fontWeight: 500, color: '#5B3FFF', whiteSpace: 'nowrap', letterSpacing: '-0.25px' }}>
              {t('TaskManagemnt.confirmGuestCheckout')}
            </Typography>
          </Box>
          <IconButton size="small" sx={{ p: '4px' }} onClick={(e) => { e.stopPropagation(); onRowClick?.(item); }}>
            <FiEdit3 size={18} color="#8E98A1" />
          </IconButton>
        </Box>
      </Box>
    );
  }

  // vacant tab
  return (
    <Box sx={rowSx}>
      <PropertyCellWithCheckbox item={item} selected={selected} onSelect={onSelect} flex="2" />
      <PropertyStatusCell item={item} flex="1.5" />
      {/* Last Checkout — date + time pill side by side (horizontal, matches Figma) */}
      <Box sx={{ flex: '2', minWidth: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '13px', color: '#435363' }}>
          {item.lastCheckOutDisplay ? formatDate(item.checkOut) : '—'}
        </Typography>
        {item.checkOutTimeDisplay && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: '#F5F5F5', px: '8px', py: '2px', borderRadius: '12px' }}>
            <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '12px', color: '#435363' }}>
              {formatTimeDisplay(item.checkOutTimeDisplay)}
            </Typography>
            <LuClock size={12} color="#8E98A1" />
          </Box>
        )}
      </Box>
      {/* Next Reservation */}
      <Box sx={{ flex: '2', minWidth: 0 }}>
        <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '13px', color: '#435363' }}>
          {item.nextCheckInDisplay || t('TaskManagemnt.noReservation')}
        </Typography>
      </Box>
      {/* Vacant Duration */}
      <Box sx={{ flex: '1.5', minWidth: 0 }}>
        <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '13px', color: '#435363' }}>
          {item.vacantDurationDisplay || '—'}
        </Typography>
      </Box>
      {/* Create Direct Booking — purple outlined button per Figma */}
      <Box sx={{ flex: '2', display: 'flex', alignItems: 'center' }}>
        <Box
          onClick={(e) => {
            e.stopPropagation();
            if (onCreateDirectBooking) {
              const vacantItem: ITaskManagementDashboardItem = {
                id: item.id,
                reservationId: item.reservationId,
                propertyId: item.propertyId,
                propertyName: item.propertyName,
                checkIn: '',
                checkOut: '',
                guestName: '',
                guestPhoneNumber: '',
                platFormSRC: item.platFormSRC,
                channelName: item.channelName,
                reservationDate: '',
                propertyLocation: '',
                propertyImage: '',
                numberOfNights: 0,
                totalPrice: 0,
                reservationNumber: item.reservationId,
                bookingStatus: 'vacant',
                createOn: '',
                modifiedOn: '',
                isMessageSend: false,
                ownerId: 0,
                statusEnglish: null,
                statusAR: null,
                checkInDateTime: '',
                checkOutDateTime: '',
              };
              onCreateDirectBooking(vacantItem);
            }
          }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: '24px',
            height: '32px',
            borderRadius: '10px',
            border: '1px solid #5B3FFF',
            cursor: 'pointer',
            '&:hover': { backgroundColor: '#F3F0FF' },
          }}
        >
          <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '12px', fontWeight: 500, color: '#5B3FFF', whiteSpace: 'nowrap', letterSpacing: '-0.25px' }}>
            {t('TaskManagemnt.createDirectBooking')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// --- Pagination ---
const Pagination = ({
  totalCount,
  activePage,
  perPage,
  onPageChange,
  onPerPageChange,
}: {
  totalCount: number;
  activePage: number;
  perPage: string;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: string) => void;
}) => {
  const { t } = useTranslation();
  const [perPageOpen, setPerPageOpen] = useState(false);
  const totalPages = Math.ceil(totalCount / Number(perPage));
  const perPageOptions = ['4', '10', '20', '50'];

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1, 2, 3);
      if (activePage > 4) pages.push('...');
      if (activePage > 3 && activePage < totalPages - 2) pages.push(activePage);
      if (activePage < totalPages - 3) pages.push('...');
      pages.push(totalPages - 1, totalPages);
    }
    return [...new Set(pages)];
  };

  if (totalPages <= 1) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '13px',
        height: '40px',
        mt: 2,
      }}
    >

      {/* Page navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {/* Previous */}
        <Box
          onClick={() => activePage > 1 && onPageChange(activePage - 1)}
          sx={{
            width: '24px',
            height: '24px',
            borderRadius: '5px',
            backgroundColor: activePage > 1 ? '#EEEBFF' : '#E9E9E9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: activePage > 1 ? 'pointer' : 'default',
            boxShadow: '0px 0px 6px 0px rgba(0,0,0,0.02), 0px 2px 4px 0px rgba(0,0,0,0.08)',
          }}
        >
          <IoChevronForward size={14} color="#8E98A1" />

        </Box>

        {/* Page numbers */}
        {getPageNumbers().map((page, i) => (
          <Box
            key={i}
            onClick={() => typeof page === 'number' && onPageChange(page)}
            sx={{
              width: '24px',
              height: '24px',
              borderRadius: '5px',
              backgroundColor: page === activePage ? '#5B3FFF' : '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: typeof page === 'number' ? 'pointer' : 'default',
              boxShadow: '0px 0px 6px 0px rgba(0,0,0,0.02), 0px 2px 4px 0px rgba(0,0,0,0.08)',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Tajawal, sans-serif',
                fontSize: '14px',
                fontWeight: page === activePage ? 500 : 400,
                color: page === activePage ? '#FFFFFF' : '#5B3FFF',
              }}
            >
              {page}
            </Typography>
          </Box>
        ))}

        {/* Next */}
        <Box
          onClick={() => activePage < totalPages && onPageChange(activePage + 1)}
          sx={{
            width: '24px',
            height: '24px',
            borderRadius: '5px',
            backgroundColor: activePage < totalPages ? '#E9E9E9' : '#E9E9E9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: activePage < totalPages ? 'pointer' : 'default',
            boxShadow: '0px 0px 6px 0px rgba(0,0,0,0.02), 0px 2px 4px 0px rgba(0,0,0,0.08)',
          }}
        >
          <IoChevronBack size={14} color={activePage > 1 ? '#5B3FFF' : '#8E98A1'} />
        </Box>
      </Box>

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      <Typography
        sx={{
          fontFamily: 'Tajawal, sans-serif',
          fontSize: '14px',
          color: '#8E98A1',
        }}
      >
        {t('TaskManagemnt.resultsPerPage')}
      </Typography>
      {/* Per page dropdown */}
      <Box sx={{ position: 'relative' }}>
        <Box
          onClick={() => setPerPageOpen((prev) => !prev)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            border: '1px solid #E9E9E9',
            borderRadius: '8px',
            px: '12px',
            height: '40px',
            cursor: 'pointer',
          }}
        >
          <IoChevronDown size={16} color="#8E98A1" />
          <Typography
            sx={{
              fontFamily: 'Tajawal, sans-serif',
              fontSize: '14px',
              color: '#8E98A1',
            }}
          >
            {perPage}
          </Typography>
        </Box>
        {perPageOpen && (
          <Box
            sx={{
              position: 'absolute',
              bottom: '44px',
              left: 0,
              backgroundColor: '#FFFFFF',
              border: '1px solid #E9E9E9',
              borderRadius: '8px',
              boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
              zIndex: 10,
              overflow: 'hidden',
            }}
          >
            {perPageOptions.map((opt) => (
              <Box
                key={opt}
                onClick={() => {
                  onPerPageChange(opt);
                  onPageChange(1);
                  setPerPageOpen(false);
                }}
                sx={{
                  px: '16px',
                  py: '8px',
                  cursor: 'pointer',
                  backgroundColor: opt === perPage ? '#F3F0FF' : 'transparent',
                  '&:hover': { backgroundColor: '#F3F0FF' },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Tajawal, sans-serif',
                    fontSize: '14px',
                    color: opt === perPage ? '#5B3FFF' : '#435363',
                    fontWeight: opt === perPage ? 600 : 400,
                  }}
                >
                  {opt}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>




    </Box>
  );
};

// --- Main Table Component ---
const ReservationTable = ({
  activeTab,
  items,
  totalCount,
  isLoading,
  activePage,
  perPage,
  onPageChange,
  onPerPageChange,
  onRowClick,
  onConfirmCheckout,
  isConfirmingCheckout,
  onCreateDirectBooking,
  searchValue,
  onSearchChange,
  bookingStatusFilter,
  onBookingStatusChange,
  propertyIdFilter,
  onPropertyIdChange,
}: IReservationTableProps) => {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [confirmItem, setConfirmItem] = useState<IReservationListItem | null>(null);

  const handleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((item) => item.id)));
    }
  };

  const handleSelectRow = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: '16px' }}>
          {/* Title */}
          <Typography
            sx={{
              fontFamily: 'Tajawal, sans-serif',
              fontWeight: 400,
              fontSize: '24px',
              color: '#435363',
              textAlign: 'start',
              mb: 2,
            }}
          >
            {t('TaskManagemnt.reservationList')}
          </Typography>

          {/* Filters */}
          <Box sx={{ mb: 2 }}>
            <ReservationFilters
              activeTab={activeTab}
              searchValue={searchValue}
              onSearchChange={onSearchChange}
              bookingStatusFilter={bookingStatusFilter}
              onBookingStatusChange={onBookingStatusChange}
              propertyIdFilter={propertyIdFilter}
              onPropertyIdChange={onPropertyIdChange}
            />
          </Box>

          {/* Table */}
          <Box sx={{ width: '100%' }}>
            <Box>
              <TableHeader allSelected={items.length > 0 && selectedIds.size === items.length} onSelectAll={handleSelectAll} activeTab={activeTab} />

              {/* Loading */}
              {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <div
                    className="animate-spin rounded-full h-8 w-8"
                    style={{ border: '2px solid #E8E8EA', borderTopColor: '#5B3FFF' }}
                  />
                </Box>
              )}

              {/* Empty state */}
              {!isLoading && items.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Tajawal, sans-serif',
                      fontSize: '16px',
                      color: '#8E98A1',
                    }}
                  >
                    {t('TaskManagemnt.noReservations')}
                  </Typography>
                </Box>
              )}

              {/* Rows */}
              {!isLoading &&
                items.map((item, i) => (
                  <TableRow key={item.id || i} item={item} selected={selectedIds.has(item.id)} onSelect={() => handleSelectRow(item.id)} onRowClick={onRowClick} onShowConfirmModal={(item) => setConfirmItem(item)} onCreateDirectBooking={onCreateDirectBooking} activeTab={activeTab} />
                ))}
            </Box>
          </Box>

          {/* Pagination */}
          <Pagination
            totalCount={totalCount}
            activePage={activePage}
            perPage={perPage}
            onPageChange={onPageChange}
            onPerPageChange={onPerPageChange}
          />
        </Box>
      </Box>

      {/* Confirm Checkout Confirmation Modal */}
      <ConfirmCheckoutModal
        open={!!confirmItem}
        onClose={() => setConfirmItem(null)}
        onConfirm={async () => {
          if (!confirmItem?.id) return;
          await onConfirmCheckout?.(confirmItem.id);
          setConfirmItem(null);
        }}
        guestName={confirmItem?.guestName ?? null}
        propertyName={confirmItem?.propertyName ?? null}
        isConfirming={!!isConfirmingCheckout}
      />
    </>
  );
};

export default ReservationTable;
